import React, { useState, useEffect } from "react";
import { Option } from "react-dropdown";
import { useDispatch, useSelector } from "react-redux";
import { deleteFolders, fetchMaxRetentionPeriodLimit } from "src/actionCreators/folderActionCreators";
import { AddFolderIcon, ChangeRetentionPeriodIcon, DeleteIcon, EditIcon, MoveToIcon } from "src/assets/images/svg/IconCollection";
import { ChangeRetentionPeriodModal } from "src/components/Common/Modal/ChangeRetentionPeriodModal";
import { CreateFolderModal } from "src/components/Common/Modal/CreateFolderModal";
import { CustomModal } from "src/components/Common/Modal/CustomModal";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import { FolderToolbarConstants } from "src/Core/Common/Constants";
import { FolderEventType, ShareFolderTypes } from "src/Core/Common/Enums";
import { IFolderModel } from "src/Core/ViewModels/Folder/FolderModel";
import { enableHeaderAction, getDaysFromRetentionPeriodId, isFolderOwner } from "src/helper/HelperFunctions";
import { ResourceIdLocators } from "src/helper/ResourceIdLocators";
import { ApplicationState } from "src/store";
import { getRetentionOptions } from "../../../helper/HelperFunctions";
import { ShareFolderModal } from "src/components/Folders/ShareFolderModal";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { ICompanyRetentionSettings, IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";
import { IUserRetentionSettings } from "src/Core/ViewModels/User/UserViewModel";

interface IFolderToolbarProps {
    isAnySelected?: boolean;
    selectedFolders?: IFolderModel[];
    retentionOptions?: Option[];
    reset?: boolean;
    currentFolder?: IFolderModel;
    showRenameOption?: boolean;
    isMoveToFolderEnabled?: boolean;
    isSearched?: boolean;
    setReload?: (value: boolean) => void;
    resetSelection?: () => void;
    openMoveToFolder?: () => void;
}

const FolderToolbar: React.FC<IFolderToolbarProps> = props => {
    const dispatch = useDispatch();
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDelete, toggleShowDelete] = useState(false);
    const [showCreateFolder, toggleShowCreateFolder] = useState(false);
    const [showChangeRetentionModal, setShowChangeRetentionModal] = useState(false);
    const [retentionPeriods, setRetentionPeriods] = useState<Option[]>([]);
    const [newlyCreatedFolder, setNewlyCreatedFolder] = useState<IFolderModel>();
    const [showShareFolderPopup, setShowShareFolderPopup] = useState(false);

    const user = useSelector((state: ApplicationState) => state.userProfile);
    const mailboxData = useSelector((state: ApplicationState) => state.commonState.mailboxUserData);
    const retentionData = useSelector((appState: ApplicationState) => appState.folderState.retentionPeriodsData);
    const deleteFolderBodyMessage =
        props.selectedFolders.length > 1 ? FolderToolbarConstants.DELETE_FOLDERS : FolderToolbarConstants.DELETE_FOLDER;
    const deleteFolderTitle =
        props.selectedFolders.length > 1
            ? FolderToolbarConstants.TITLE_DELETE_FOLDERS
            : FolderToolbarConstants.TITLE_DELETE_FOLDER;
    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const { userSettings } = useSelector((appState: ApplicationState) => appState.userSettingsState);

    useEffect(() => {
        const folderRetention = getMaxFolderRetention(retentionData.retentionPeriods, props.currentFolder, messageOptionsSettings.retentionSettings, userSettings?.retentionSettings);
        setRetentionPeriods(getRetentionOptions(folderRetention));
    }, [retentionData.retentionPeriods.length, props.currentFolder.folderId, props.currentFolder.expiresDays, userSettings?.retentionSettings.defaultRetentionPeriods, messageOptionsSettings.retentionPeriodList, messageOptionsSettings.retentionSettings]);

    useEffect(() => {
        toggleShowDelete(false);
    }, [props.reset]);

    const getMaxFolderRetention = (
        retentionPeriods: IRetentionPeriod[],
        currentFolder: IFolderModel,
        companyRetentionSettings?: ICompanyRetentionSettings,
        userRetentionSettings?: IUserRetentionSettings,
    ) => {
        let folderRetention = retentionPeriods;
        let maxRetention = 0;
        if (companyRetentionSettings?.isUserAllowedToChangeFolderRetentionPeriod) {
            if (currentFolder.folderId === 0) {
                maxRetention = getDaysFromRetentionPeriodId(
                    userRetentionSettings?.defaultRetentionPeriods.folders ?? 0,
                    retentionPeriods
                );
            } else {
                maxRetention = currentFolder.expiresDays;
            }
            folderRetention = retentionPeriods.filter(rp => rp.days <= maxRetention);
        }
        return folderRetention;
    };

    const handleOnClickDelete = () => {
        toggleShowDelete(true);
    };

    const handleOnClickCreateFolder = () => {
        toggleShowCreateFolder(true);
    };

    const handleOnClickChangeRetention = () => {
        if (props.currentFolder.folderId === 0) {
            const folderRetention = getMaxFolderRetention(retentionData.retentionPeriods, props.currentFolder, messageOptionsSettings.retentionSettings, userSettings?.retentionSettings);
            setRetentionPeriods(getRetentionOptions(folderRetention));
            setShowChangeRetentionModal(true);
        } else {
            dispatch(
                fetchMaxRetentionPeriodLimit(props.selectedFolders[0].folderId, (result: number) => {
                    setRetentionPeriods(
                        getRetentionOptions(retentionData.retentionPeriods.filter(rp => rp.days <= result))
                    );
                    setShowChangeRetentionModal(true);
                })
            );
        }
    };

    const handleChangeRetentionSubmit = () => {
        setShowChangeRetentionModal(false);
        props.resetSelection();
        props.setReload(true);
    };

    const handleOnSubmitDelete = () => {
        dispatch(
            deleteFolders(
                props.selectedFolders.map(f => f.folderId),
                result => {
                    if (result) {
                        AppNotifier.Success(
                            props.selectedFolders.length > 1
                                ? ToasterMessages.SUCCESS.DELETE_FOLDERS
                                : ToasterMessages.SUCCESS.DELETE_FOLDER
                        );
                    }
                    toggleShowDelete(false);
                    props.resetSelection();
                    props.setReload(true);
                },
                ResourceIdLocators.MyFiles.MyFilesBtnDeleteFolders
            )
        );
    };

    const handleRenameModalSubmition = () => {
        setShowRenameModal(false);
        props.setReload(true);
        props.resetSelection();
    };

    const handleCreateFolderSubmit = (folder: IFolderModel | undefined, showSharedFolderPopUp?: boolean) => {
        if (folder) {
            if (folder.parentId === (props.currentFolder?.folderId || 0)) {
                props.setReload(true);
            }
            setNewlyCreatedFolder(folder);
        }
        props.setReload(false);
        toggleShowCreateFolder(false);
        showSharedFolderPopUp && setShowShareFolderPopup(showSharedFolderPopUp);
    };

    const handleOnClickMoveToFolder = e => {
        e.stopPropagation();
        props.openMoveToFolder();
    };

    const enableAddButton = (folder: IFolderModel): boolean => {
        if (
            folder.folderId === 0 ||
            isFolderOwner(folder, user, mailboxData) ||
            folder.permissionGroupId === ShareFolderTypes.EDIT
        ) {
            return true;
        } else if (folder.permissionGroupId === ShareFolderTypes.NONE && !folder.isPermissiomInherited) {
            return true;
        }
        return false;
    };

    const handleRenameModalHide = () => {
        setShowRenameModal(false);
        props.resetSelection();
    };

    return (
        <>
            <div className="folder-toolbar">
                {props.showRenameOption && (
                    <div className="folder-toolbar__action">
                        <div
                            className={`pointer ${enableHeaderAction(props.selectedFolders[0], user, mailboxData)
                                ? ""
                                : "toolbar-icon-disabled"
                                }`}
                            title={FolderToolbarConstants.TITLE_EDIT}
                            onClick={() => {
                                setShowRenameModal(true);
                            }}
                        >
                            <EditIcon />
                        </div>
                    </div>
                )}

                <div className="folder-toolbar__action move-to-icon-wrapper">
                    <span
                        onClick={e => {
                            if (
                                props.isAnySelected &&
                                !props.isSearched &&
                                props.isMoveToFolderEnabled &&
                                isFolderOwner(props.selectedFolders[0], user, mailboxData)
                            ) {
                                handleOnClickMoveToFolder(e);
                            }
                        }}
                        title={FolderToolbarConstants.TITLE_MOVE_TO_FOLDER}
                        data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnMoveFolders}
                        className={
                            !props.isAnySelected ||
                                !props.isMoveToFolderEnabled ||
                                props.isSearched ||
                                !isFolderOwner(props.selectedFolders[0], user, mailboxData)
                                ? "toolbar-icon-disabled"
                                : ""
                        }
                    >
                        <MoveToIcon
                            width={18}
                            height={18}
                        />
                    </span>
                </div>

                <div className="folder-toolbar__action">
                    <span
                        onClick={() => {
                            if (
                                props.isAnySelected &&
                                enableHeaderAction(props.selectedFolders[0], user, mailboxData) &&
                                !props.isSearched
                                && messageOptionsSettings.retentionSettings?.isUserAllowedToChangeFolderRetentionPeriod
                            ) {
                                handleOnClickChangeRetention();
                            }
                        }}
                        title={FolderToolbarConstants.TITLE_CHANGE_RETENTION}
                        data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnChangeRetentions}
                        className={
                            !props.isAnySelected || props.isSearched
                                || !messageOptionsSettings.retentionSettings?.isUserAllowedToChangeFolderRetentionPeriod
                                || !enableHeaderAction(props.selectedFolders[0], user, mailboxData)
                                ? "toolbar-icon-disabled"
                                : ""
                        }
                    >
                        <ChangeRetentionPeriodIcon
                            width={15}
                            height={15}
                        />
                    </span>
                </div>

                <div className="folder-toolbar__action delete-icon-wrapper">
                    <span
                        onClick={() => {
                            if (
                                props.isAnySelected &&
                                enableHeaderAction(props.selectedFolders[0], user, mailboxData)
                            ) {
                                handleOnClickDelete();
                            }
                        }}
                        title={FolderToolbarConstants.TITLE_DELETE}
                        data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnDeleteFolders}
                        className={
                            !props.isAnySelected || !enableHeaderAction(props.selectedFolders[0], user, mailboxData)
                                ? "toolbar-icon-disabled"
                                : ""
                        }
                    >
                        <DeleteIcon
                            width={16}
                            height={16}
                        />
                    </span>
                </div>

                <div className="folder-toolbar__action">
                    <span
                        onClick={() => {
                            if (!enableAddButton(props.currentFolder)) {
                                return;
                            }
                            handleOnClickCreateFolder();
                        }}
                        className={`${!enableAddButton(props.currentFolder) ? "toolbar-icon-disabled" : ""
                            }`}
                        data-resource-id={ResourceIdLocators.MyFiles.MyFilesBtnAddFolder}
                        title={FolderToolbarConstants.TITLE_CREATE_FOLDER}
                    >
                        <AddFolderIcon width={17} height={17} />
                    </span>
                </div>
            </div>

            {showRenameModal && (
                <CreateFolderModal
                    modalType={FolderEventType.Edit}
                    show={showRenameModal}
                    retentionOptions={retentionPeriods}
                    currentFolder={props.selectedFolders[0]}
                    onHide={handleRenameModalHide}
                    onSubmit={handleRenameModalSubmition}
                    renameBtnResourceId={ResourceIdLocators.MyFiles.MyFilesDrpDwnRenameFolder}
                />
            )}

            {showChangeRetentionModal && (
                <ChangeRetentionPeriodModal
                    selectedFolders={props.selectedFolders}
                    showState={showChangeRetentionModal}
                    retentionOptions={retentionPeriods}
                    onHide={() => {
                        setShowChangeRetentionModal(false);
                        props.resetSelection();
                    }}
                    changeRtnResourceId={ResourceIdLocators.MyFiles.MyFilesBtnChangeRetentions}
                    onSubmit={handleChangeRetentionSubmit}
                />
            )}

            {showDelete && (
                <CustomModal
                    show={showDelete}
                    cancelButtonName="Cancel"
                    confirmButtonName="Confirm"
                    title={deleteFolderTitle}
                    onSubmit={handleOnSubmitDelete}
                    onHide={() => {
                        toggleShowDelete(false);
                        props.resetSelection();
                    }}
                    isConfirmModal={true}
                >
                    <div>
                        <span>{deleteFolderBodyMessage}</span>
                    </div>
                </CustomModal>
            )}
            {showCreateFolder && (
                <CreateFolderModal
                    modalType={FolderEventType.Create}
                    show={showCreateFolder}
                    retentionOptions={retentionPeriods}
                    onSubmit={handleCreateFolderSubmit}
                    currentFolder={props.currentFolder}
                    onHide={() => {
                        toggleShowCreateFolder(false);
                        props.resetSelection();
                    }}
                />
            )}
            {showShareFolderPopup && (
                <ShareFolderModal
                    show={showShareFolderPopup}
                    folder={newlyCreatedFolder}
                    onHide={() => {
                        setShowShareFolderPopup(false);
                        props.setReload(true);
                    }}
                />
            )}
        </>
    );
};

FolderToolbar.displayName = FolderToolbar.name;
export default FolderToolbar;
